import React, { useState, useRef, useEffect, useCallback } from 'react';
import { GameState } from '../types';
import { Canvas } from '../components/Canvas';
import { Toolbar } from '../components/Toolbar';
import { DrawingDisplay } from '../components/DrawingDisplay';
import { webSocketService } from '../services/websocket';

const ClockIcon = () => <i className="fa-solid fa-hourglass-half"></i>;
const RoundIcon = () => <i className="fa-solid fa-retweet"></i>;

interface GamePageProps {
    gameState: GameState;
    currentPlayerId: string;
}

export const GamePage: React.FC<GamePageProps> = ({ gameState, currentPlayerId }) => {
    const [color, setColor] = useState('#FFFFFF');
    const [lineWidth, setLineWidth] = useState(5);
    const [selectedVoteId, setSelectedVoteId] = useState<string | null>(null);
    const [displayTime, setDisplayTime] = useState(0);
    const canvasRef = useRef<any>(null);

    const submitDrawing = useCallback(() => {
        if (canvasRef.current) {
            const drawingData = canvasRef.current.getDrawingData();
            webSocketService.sendMessage({ type: 'submitDrawing', payload: drawingData });
        }
    }, []);

    useEffect(() => {
        const initialTime = gameState.gamePhase === 'drawing' ? gameState.drawingTimeLeft : gameState.votingTimeLeft;
        setDisplayTime(initialTime);

        const timer = setInterval(() => {
            setDisplayTime(prevTime => {
                const newTime = prevTime - 1;
                if (newTime <= 0 && gameState.gamePhase === 'drawing') {
                    // Time is up, automatically submit drawing
                    submitDrawing();
                }
                return Math.max(0, newTime);
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [gameState.gamePhase, gameState.drawingTimeLeft, gameState.votingTimeLeft, submitDrawing]);

    useEffect(() => {
        setSelectedVoteId(null);
    }, [gameState.currentRound]);

    const handleClear = () => {
        if (canvasRef.current) {
            canvasRef.current.clearCanvas();
        }
    };

    const handleVote = (votedPlayerId: string) => {
        if (selectedVoteId === votedPlayerId) {
            webSocketService.sendMessage({ type: 'vote', payload: { votedPlayerId } });
        } else {
            setSelectedVoteId(votedPlayerId);
        }
    };

    const handleFinishDrawing = () => {
        submitDrawing(); // Use the unified submission function
    };

    const handleNextRound = () => {
        webSocketService.sendMessage({ type: 'startNewRound' });
    };

    const handleForceEndDrawing = () => webSocketService.sendMessage({ type: 'forceEndDrawing' });
    const handleForceEndVoting = () => webSocketService.sendMessage({ type: 'forceEndVoting' });

    const renderDrawingPhase = () => {
        const currentPlayer = gameState.players.find(p => p.id === currentPlayerId);
        return (
            <>
                <div className="topic-like-container">
                    <span>題目: {gameState.currentTopic}</span>
                </div>
                <div className="canvas-container">
                    <Canvas
                        ref={canvasRef}
                        color={color}
                        lineWidth={lineWidth}
                        disabled={currentPlayer?.isFinishedDrawing || false}
                    />
                </div>
                <Toolbar
                    onColorChange={setColor}
                    onLineWidthChange={setLineWidth}
                    onClear={handleClear}
                    onFinish={handleFinishDrawing}
                    isFinished={currentPlayer?.isFinishedDrawing || false}
                    currentColor={color}
                    currentLineWidth={lineWidth}
                />
                {currentPlayer?.isHost && <button className="pixel-button danger" onClick={handleForceEndDrawing}>強制結束繪畫</button>}
            </>
        );
    };

    const renderVotingPhase = () => {
        const currentPlayer = gameState.players.find(p => p.id === currentPlayerId);

        if (currentPlayer?.hasVoted) {
            return <h4>等待其他玩家投票...</h4>;
        }

        const otherPlayers = gameState.players.filter(p => p.id !== currentPlayerId);
        return (
            <div className="voting-gallery">
                <h4>票選最佳畫作！</h4>
                {currentPlayer?.isHost && <button className="pixel-button danger" onClick={handleForceEndVoting}>強制結束投票</button>}
                <div className="gallery-grid">
                    {otherPlayers.map(player => (
                        <div key={player.id} className={`gallery-item ${selectedVoteId === player.id ? 'selected' : ''}`}>
                            <DrawingDisplay drawingData={player.drawingData || []} width={400} height={300} />
                            <button
                                className="pixel-button vote-button"
                                onClick={() => handleVote(player.id)}
                            >
                                {selectedVoteId === player.id ? '確認投票?' : `投這幅畫`}
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderResultsPhase = () => {
        const roundWinner = gameState.roundWinner;
        const sortedPlayers = [...gameState.players].sort((a, b) => b.votes - a.votes);

        return (
            <div className="results-phase">
                <h3>回合結果</h3>
                <div className="gallery-grid">
                    {sortedPlayers.map(player => (
                        <div key={player.id} className={`gallery-item ${roundWinner?.id === player.id ? 'winner' : ''}`}>
                            <h4>{player.name} {roundWinner?.id === player.id ? '👑' : ''}</h4>
                            <DrawingDisplay drawingData={player.drawingData || []} width={400} height={300} />
                            <span>得票: {player.votes}</span>
                        </div>
                    ))}
                </div>
                
                {gameState.players.find(p => p.id === currentPlayerId)?.isHost ? (
                    <button className="pixel-button" onClick={handleNextRound}>下一回合</button>
                ) : (
                    <p>等待房主開始下一回合...</p>
                )}
            </div>
        );
    };

    return (
        <div className="game-container">
            <div className="game-header">
                <div className="game-info-item"><RoundIcon /> {gameState.currentRound}</div>
                <div className="game-info-item"><ClockIcon /> {displayTime}</div>
            </div>

            {gameState.gamePhase === 'drawing' && renderDrawingPhase()}
            {gameState.gamePhase === 'voting' && renderVotingPhase()}
            {gameState.gamePhase === 'results' && renderResultsPhase()}
        </div>
    );
};